/**
 * Iter XX.224 — iOS Meta App Events dispatcher.
 *
 * Consent-first, iOS-only, fail-safe.
 *
 * PRIVACY CONTRACT (locked):
 *   · Every send is gated on `readMetaConsent() === "granted"`.
 *     Denied / null / unknown → no-op. No silent fallback path.
 *   · iOS native only. Android + web are untouched by this module.
 *   · No conversation content, video URLs, names, phone, email, or
 *     any free-form user text is ever forwarded to Meta. The
 *     allow-list below is the ONLY payload surface.
 *   · Failure in the bridge is swallowed — never blocks coaching /
 *     purchase / video review.
 *
 * NATIVE CONTRACT (documented — implemented on the founder's Mac):
 *   The dispatcher calls the existing Capacitor bridge
 *   `Capacitor.Plugins.MetaEvents.logEvent({ name, params })`. The
 *   plugin's Swift implementation must:
 *     1. Return immediately if `Settings.shared.isAdvertiserTrackingEnabled == false`.
 *     2. Call `AppEvents.shared.logEvent(...)`, mapping Gully event
 *        names to Meta standard events where appropriate (below).
 *     3. Set `_valueToSum` + `AppEvents.ParameterName.currency` for
 *        the purchase_completed path.
 *   The RevenueCat / PurchasesPlugin native code is untouched.
 *
 * Event name → Meta standard event mapping (for the Swift bridge):
 *   gully_coaching_started              → InitiatedCheckout is NOT used
 *                                          (custom event only — this is
 *                                          coaching engagement, not a
 *                                          commercial event)
 *   gully_meaningful_coaching_completed → custom event
 *   gully_plus_viewed                    → AppEvents.Name.viewedContent
 *   gully_plus_cta_tapped                → AppEvents.Name.initiatedCheckout
 *                                          (funnel signal that the user
 *                                          is progressing to checkout)
 *   gully_purchase_started               → AppEvents.Name.startTrial or
 *                                          AppEvents.Name.initiatedCheckout
 *                                          (Meta iOS SDK doesn't have a
 *                                          "purchase_started" — Swift
 *                                          bridge must NOT log this as
 *                                          `purchased`)
 *   gully_purchase_completed             → AppEvents.Name.purchased
 *                                          + _valueToSum + currency
 *                                          + AppEvents.Name.subscribe
 *                                          (Meta subscription event)
 *   gully_purchase_cancelled             → custom event only
 *                                          (Meta has no standard for this)
 *   gully_video_review_started           → custom event
 *   gully_video_review_completed         → custom event
 *
 * De-duplication: purchase_completed is deduped by transaction_id in a
 * per-install localStorage set. Meta doesn't dedupe purchase events on
 * iOS custom-event pipelines.
 */
import { isIOSNative, readMetaConsent } from "./metaConsent";


// ----- Allow-list of parameter keys the dispatcher will forward -----
// Anything not in this set is dropped SILENTLY before the bridge call.
// This is the single privacy guardrail against a caller accidentally
// leaking chat text or user identity.
const _ALLOWED_KEYS = new Set([
  "platform",           // literal string "ios"
  "surface",            // e.g. "chat_header", "upgrade_page"
  "trigger",            // e.g. "return_after_experiment", "coaching_outcome_completed"
  "cta_variant",        // Iter XX.223 propagation
  "product_id",         // e.g. "plus_monthly_ios"
  "period",             // e.g. "monthly"
  "value",              // number — currency-neutral price
  "currency",           // ISO 4217 code
  "transaction_id",     // for dedupe only — Meta doesn't ingest
  "cancel_reason",      // "user_cancelled" | "pending"
  "conversion_context_id",  // Iter XX.223 funnel id
]);


const _DEDUP_KEY = "gully_ios_meta_events_dedupe_v1";


function _readDedupe() {
  try {
    return new Set(JSON.parse(localStorage.getItem(_DEDUP_KEY) || "[]"));
  } catch {
    return new Set();
  }
}


function _writeDedupe(set) {
  try {
    // Keep the set bounded to the last 200 transactions — enough for a
    // normal player's lifetime IAP history, never grows unbounded.
    const arr = Array.from(set).slice(-200);
    localStorage.setItem(_DEDUP_KEY, JSON.stringify(arr));
  } catch { /* localStorage unavailable — best effort */ }
}


function _sanitize(params) {
  if (!params || typeof params !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(params)) {
    if (!_ALLOWED_KEYS.has(k)) continue;
    if (v === undefined || v === null) continue;
    // Reject any string field that looks like free-form text.
    if (typeof v === "string" && v.length > 80) continue;
    out[k] = v;
  }
  // Always stamp platform literally — never taken from caller input.
  out.platform = "ios";
  return out;
}


async function _bridgeCall(name, params) {
  // Capacitor bridge is optional at build time; guarded so missing
  // plugin never throws.
  //
  // Iter XX.269 — Xcode-visible breadcrumbs. Every send prints a
  // `[XX.269-meta]` line so the founder can verify the exact stage
  // from Xcode device log without needing Safari Web Inspector.
  try {
    const cap = (typeof window !== "undefined") && window.Capacitor;
    const plugin = cap?.Plugins?.MetaEvents;
    if (!plugin?.logEvent) {
      try {
        // eslint-disable-next-line no-console
        console.info("[XX.269-meta] logEvent plugin_missing", { name });
      } catch { /* noop */ }
      return { ok: false, reason: "plugin_missing" };
    }
    try {
      // eslint-disable-next-line no-console
      console.info("[XX.269-meta] logEvent invoking", { name, param_keys: Object.keys(params || {}) });
    } catch { /* noop */ }
    await plugin.logEvent({ name, params });
    try {
      // eslint-disable-next-line no-console
      console.info("[XX.269-meta] logEvent ok", { name });
    } catch { /* noop */ }
    return { ok: true };
  } catch (e) {
    try {
      // eslint-disable-next-line no-console
      console.info("[XX.269-meta] logEvent threw",
        { name, error: String(e?.message || e) });
    } catch { /* noop */ }
    return { ok: false, reason: "plugin_error" };
  }
}


/**
 * Public: fire a Gully Meta App Event. Consent-first, iOS-only.
 * Every non-ok result is swallowed — callers do NOT need to try/catch.
 */
export async function logGullyMetaEvent(name, params = {}) {
  try {
    if (!name || typeof name !== "string") return { ok: false, reason: "bad_name" };
    if (!isIOSNative()) {
      // Iter XX.269 — Xcode-visible breadcrumb.
      try {
        // eslint-disable-next-line no-console
        console.info("[XX.269-meta] gate not_ios", { name });
      } catch { /* noop */ }
      return { ok: false, reason: "not_ios" };
    }
    // HARD gate — no consent, no event. Ever. No exceptions.
    if (readMetaConsent() !== "granted") {
      try {
        // eslint-disable-next-line no-console
        console.info("[XX.269-meta] gate consent_not_granted", {
          name, decision_raw: readMetaConsent(),
        });
      } catch { /* noop */ }
      return { ok: false, reason: "consent_not_granted" };
    }
    const safe = _sanitize(params);

    // Dedupe purchase_completed by transaction id.
    if (name === "gully_purchase_completed" && safe.transaction_id) {
      const dedupe = _readDedupe();
      if (dedupe.has(safe.transaction_id)) {
        return { ok: false, reason: "duplicate_purchase" };
      }
      dedupe.add(safe.transaction_id);
      _writeDedupe(dedupe);
    }

    // Strip transaction_id before bridging — dedupe-only field.
    const forward = { ...safe };
    delete forward.transaction_id;

    return await _bridgeCall(name, forward);
  } catch {
    return { ok: false, reason: "unexpected" };
  }
}


// ----- Convenience wrappers used at instrumentation sites -----

export function logCoachingStarted(surface) {
  return logGullyMetaEvent("gully_coaching_started", { surface });
}
export function logMeaningfulCoachingCompleted(trigger) {
  return logGullyMetaEvent("gully_meaningful_coaching_completed", { trigger });
}
export function logPlusViewed(surface) {
  return logGullyMetaEvent("gully_plus_viewed", { surface });
}
export function logPlusCtaTapped({ trigger, cta_variant, conversion_context_id }) {
  return logGullyMetaEvent("gully_plus_cta_tapped", {
    trigger, cta_variant, conversion_context_id,
  });
}
export function logPurchaseStarted({ product_id, period, surface }) {
  return logGullyMetaEvent("gully_purchase_started", {
    product_id, period, surface,
  });
}
export function logPurchaseCompleted({ product_id, period, value, currency, transaction_id }) {
  return logGullyMetaEvent("gully_purchase_completed", {
    product_id, period, value, currency, transaction_id,
  });
}
export function logPurchaseCancelled({ product_id, cancel_reason }) {
  return logGullyMetaEvent("gully_purchase_cancelled", {
    product_id, cancel_reason,
  });
}
export function logVideoReviewStarted() {
  return logGullyMetaEvent("gully_video_review_started", {});
}
export function logVideoReviewCompleted() {
  return logGullyMetaEvent("gully_video_review_completed", {});
}


// ----- Test hooks (never invoked in production code) -----
export const __ALLOWED_KEYS_FOR_TESTS = Array.from(_ALLOWED_KEYS);
export function __resetDedupeForTests() {
  try { localStorage.removeItem(_DEDUP_KEY); } catch {}
}
